import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth, isAdmin, isPartner, isBO } from '@/contexts/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, UserCheck, UserCog, UserX, Shield, Users as UsersIcon } from 'lucide-react';

const statusBadge = { pending: { v: 'outline', l: 'In attesa' }, active: { v: 'default', l: 'Attivo' }, disabled: { v: 'destructive', l: 'Disabilitato' } };
const roleLabel = { admin: 'Super Admin', partner: 'B.O.', area_manager: 'Area Manager', agent: 'Agente' };

const emptyUser = { full_name: '', email: '', phone: '', password: '', role: 'agent', company_id: '', status: 'active', manager_id: null };

export default function Users() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyUser);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([api.listUsers(), isAdmin(me) ? api.listCompanies() : Promise.resolve([])]);
      setItems(u); setCompanies(c);
    } catch (e) { toast.error('Errore caricamento'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const companiesMap = useMemo(() => Object.fromEntries(companies.map(c => [c.id, c])), [companies]);

  // Elenco Area Manager per la company selezionata nel form (usato per il dropdown manager_id)
  const managersForForm = useMemo(() => {
    const cid = form.company_id;
    if (!cid) return [];
    return items.filter(u => u.role === 'area_manager' && u.company_id === cid && u.status === 'active');
  }, [items, form.company_id]);

  const usersMap = useMemo(() => Object.fromEntries(items.map(u => [u.id, u])), [items]);

  const filtered = items.filter(u => {
    if (filterStatus !== 'all' && u.status !== filterStatus) return false;
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (search) {
      const t = search.toLowerCase();
      if (![u.full_name, u.email].some(s => (s || '').toLowerCase().includes(t))) return false;
    }
    return true;
  });

  const handleNew = () => {
    setEditId(null);
    setForm({
      ...emptyUser,
      role: isPartner(me) ? 'agent' : 'agent',
      company_id: isPartner(me) ? me.company_id : (companies[0]?.id || ''),
    });
    setOpen(true);
  };
  const handleEdit = (u) => { setEditId(u.id); setForm({ ...emptyUser, ...u, password: '' }); setOpen(true); };

  const handleSave = async () => {
    try {
      if (editId) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.updateUser(editId, payload);
        toast.success('Utente aggiornato');
      } else {
        if (!form.password || form.password.length < 6) { toast.error('Password minima 6 caratteri'); return; }
        await api.createUser(form);
        toast.success('Utente creato');
      }
      setOpen(false); setForm(emptyUser); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const handleApprove = async (u) => {
    // For pending agents without company_id, open the edit dialog so admin can assign a partner company first
    if (!u.company_id && isAdmin(me)) {
      setEditId(u.id);
      setForm({ ...emptyUser, ...u, password: '', status: 'active', company_id: '' });
      setOpen(true);
      toast.info(`Assegna l'azienda partner corretta a "${u.full_name}" (dichiarata: ${u.pending_company_name || '—'}) e poi salva per attivare.`);
      return;
    }
    try { await api.updateUser(u.id, { status: 'active' }); toast.success('Utente approvato'); load(); } catch { toast.error('Errore'); }
  };
  const handleDisable = async (u) => {
    try { await api.updateUser(u.id, { status: 'disabled' }); toast.success('Utente disabilitato'); load(); } catch { toast.error('Errore'); }
  };
  const handleDelete = async (u) => { try { await api.deleteUser(u.id); toast.success('Eliminato'); load(); } catch { toast.error('Errore'); } };

  return (
    <div>
      <PageHeader
        title="Utenti"
        description={isPartner(me) ? (isBO(me) ? 'Gestisci Area Manager e Agenti della tua azienda' : 'Gestisci gli agenti della tua azienda') : 'Gestisci Super Admin, B.O., Area Manager e Agenti'}
        actions={<Button onClick={handleNew} data-testid="users-create-button"><Plus className="size-4 mr-2" />Nuovo utente</Button>}
      />

      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b flex flex-col md:flex-row md:items-center gap-3">
            <Input placeholder="Cerca nome, email..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" data-testid="users-search-input" />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="users-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem><SelectItem value="pending">In attesa</SelectItem><SelectItem value="active">Attivo</SelectItem><SelectItem value="disabled">Disabilitato</SelectItem></SelectContent>
            </Select>
            {isAdmin(me) && (
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="w-full md:w-[200px]" data-testid="users-filter-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i ruoli</SelectItem>
                  <SelectItem value="admin">Super Admin</SelectItem>
                  <SelectItem value="partner">B.O.</SelectItem>
                  <SelectItem value="area_manager">Area Manager</SelectItem>
                  <SelectItem value="agent">Agente</SelectItem>
                </SelectContent>
              </Select>
            )}
            <div className="flex-1" />
            <div className="text-xs text-muted-foreground">{filtered.length} utenti</div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-10 text-center">
              <div className="mx-auto size-12 rounded-full bg-secondary grid place-items-center mb-3"><UsersIcon className="size-5 text-muted-foreground" /></div>
              <div className="text-sm font-medium">{loading ? 'Caricamento...' : 'Nessun utente'}</div>
            </div>
          ) : (
            <Table data-testid="users-table">
              <TableHeader><TableRow>
                <TableHead>Utente</TableHead><TableHead>Email</TableHead><TableHead>Ruolo</TableHead>
                {isAdmin(me) && <TableHead>Azienda</TableHead>}<TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map(u => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell><div className="font-medium">{u.full_name || '-'}</div><div className="text-xs text-muted-foreground">{u.phone}</div>{u.status === 'pending' && u.pending_company_name && <div className="text-xs mt-1"><span className="text-muted-foreground">Azienda dichiarata:</span> <span className="font-medium text-foreground">{u.pending_company_name}</span></div>}</TableCell>
                    <TableCell className="text-sm font-mono">{u.email}</TableCell>
                    <TableCell><Badge variant="secondary">{roleLabel[u.role] || u.role}</Badge></TableCell>
                    {isAdmin(me) && <TableCell className="text-sm">{companiesMap[u.company_id]?.name || (u.role === 'admin' ? '-' : '—')}</TableCell>}
                    <TableCell><Badge variant={statusBadge[u.status]?.v || 'outline'}>{statusBadge[u.status]?.l || u.status}</Badge></TableCell>
                    <TableCell className="text-right space-x-1">
                      {u.status === 'pending' && (
                        <Button size="sm" variant="default" onClick={() => handleApprove(u)} data-testid={`user-approve-${u.id}`}><UserCheck className="size-4 mr-1" />Approva</Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(u)} data-testid={`user-edit-${u.id}`}><Pencil className="size-4" /></Button>
                      {u.status === 'active' && u.id !== me.id && (
                        <Button variant="ghost" size="icon" onClick={() => handleDisable(u)} data-testid={`user-disable-${u.id}`}><UserX className="size-4" /></Button>
                      )}
                      {u.id !== me.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="icon" data-testid={`user-delete-${u.id}`}><Trash2 className="size-4 text-destructive" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Eliminare l’utente?</AlertDialogTitle><AlertDialogDescription>Azione irreversibile.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(u)}>Elimina</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editId ? 'Modifica utente' : 'Nuovo utente'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nome e Cognome *</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-full-name-input" /></div>
            <div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editId} data-testid="user-email-input" /></div>
            <div className="space-y-1.5"><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Password {editId && <span className="text-xs text-muted-foreground">(lascia vuoto per non cambiare)</span>}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
            {isAdmin(me) && (
              <>
                <div className="space-y-1.5"><Label>Ruolo *</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Super Admin</SelectItem>
                      <SelectItem value="partner">B.O. (Back Office)</SelectItem>
                      <SelectItem value="area_manager">Area Manager</SelectItem>
                      <SelectItem value="agent">Agente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Azienda</Label>
                  <Select value={form.company_id || 'none'} onValueChange={(v) => setForm({ ...form, company_id: v === 'none' ? null : v })}>
                    <SelectTrigger data-testid="user-company-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Nessuna (solo admin)</SelectItem>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  {form.pending_company_name && !form.company_id && (
                    <div className="text-xs mt-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-amber-900">
                      <span className="font-medium">Dichiarata dall’agente in registrazione:</span> {form.pending_company_name}
                    </div>
                  )}
                </div>
              </>
            )}
            {isBO(me) && !editId && (
              <div className="space-y-1.5"><Label>Ruolo *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="area_manager">Area Manager</SelectItem>
                    <SelectItem value="agent">Agente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5"><Label>Stato</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="user-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="pending">In attesa</SelectItem><SelectItem value="active">Attivo</SelectItem><SelectItem value="disabled">Disabilitato</SelectItem></SelectContent>
              </Select>
            </div>
            {form.role === 'agent' && (isAdmin(me) || isBO(me)) && (
              <div className="space-y-1.5" data-testid="agent-manager-block">
                <Label>Area Manager di riferimento</Label>
                <Select
                  value={form.manager_id || 'none'}
                  onValueChange={(v) => setForm({ ...form, manager_id: v === 'none' ? null : v })}
                >
                  <SelectTrigger data-testid="agent-manager-select">
                    <SelectValue placeholder={managersForForm.length === 0 ? 'Nessun Area Manager disponibile' : 'Nessuno / diretto'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno (diretto)</SelectItem>
                    {managersForForm.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.full_name || m.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {managersForForm.length === 0 && form.company_id && (
                  <p className="text-xs text-muted-foreground">Nessun Area Manager attivo per questa azienda. Creane uno con ruolo &quot;Area Manager&quot;.</p>
                )}
              </div>
            )}
            {form.role === 'agent' && (
              <div className="md:col-span-2 rounded-lg border p-3 bg-secondary/30 space-y-3" data-testid="agent-permissions-block">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Permessi agente</div>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label className="text-sm">Può inviare email a clienti</Label>
                    <p className="text-xs text-muted-foreground">Invio di preventivi e contratti via email dal gestionale.</p>
                  </div>
                  <Switch
                    checked={(form.agent_permissions?.can_send_email) !== false}
                    onCheckedChange={(v) => setForm({ ...form, agent_permissions: { ...(form.agent_permissions || {}), can_send_email: v } })}
                    data-testid="agent-perm-send-email"
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label className="text-sm">Può vedere i prezzi di listino</Label>
                    <p className="text-xs text-muted-foreground">Se disattivato, i prezzi sono nascosti nel catalogo; i PDF vengono comunque calcolati con i prezzi corretti.</p>
                  </div>
                  <Switch
                    checked={(form.agent_permissions?.can_view_prices) !== false}
                    onCheckedChange={(v) => setForm({ ...form, agent_permissions: { ...(form.agent_permissions || {}), can_view_prices: v } })}
                    data-testid="agent-perm-view-prices"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={handleSave} data-testid="user-save-button">{editId ? 'Salva' : 'Crea'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
